import type { Scene } from "@babylonjs/core/scene";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";

/**
 * One shader for all plants (colour comes from per-vertex data, so a single
 * material instances every species). Adds the organic look kit:
 *  - coherent global wind: one wind field, phase from world position, amplitude
 *    ∝ height above the plant base — the whole ecosystem sways as one.
 *  - leaf translucency: a backlight term so leaves glow when between you and the
 *    low sun (no specular anywhere, per the design rule).
 *  - colour from state: water-stress tints toward dry yellow; a gentle
 *    height-based ambient occlusion darkens the base.
 * Growth itself is the per-instance world-matrix scale set by the renderer.
 */
Effect.ShadersStore["plantVertexShader"] = /* glsl */ `
precision highp float;
attribute vec3 position;
attribute vec3 normal;
attribute vec4 color;
attribute float birthLength;   // normalized height from base, 0..1
attribute vec4 world0;
attribute vec4 world1;
attribute vec4 world2;
attribute vec4 world3;
attribute float instStress;
attribute float instPhase;
attribute float instGrowth;    // per-plant growth front, 0..1
attribute float instLeanX;     // world-space phototropic lean
attribute float instLeanZ;

uniform mat4 viewProjection;
uniform float time;
uniform float windStrength;

varying vec3 vNormal;
varying vec4 vColor;
varying float vStress;
varying float vAO;

void main() {
  mat4 world = mat4(world0, world1, world2, world3);

  // --- base-to-tip growth: a front sweeps up birthLength; vertices ahead of it
  //     collapse onto the growing tip (the vertical axis at height F), and thicken
  //     in as the plant matures (acropetal unfurl). ---
  float F = instGrowth;
  float b = birthLength;
  float reveal = smoothstep(b - 0.18, b, F);
  float thick = sqrt(clamp(F, 0.0, 1.0));
  vec3 adult = vec3(position.x * thick, position.y, position.z * thick);
  float axisY = (b > 0.001) ? F * position.y / b : 0.0;
  vec3 grown = mix(vec3(0.0, axisY, 0.0), adult, reveal);

  vec4 wp = world * vec4(grown, 1.0);
  float base = world3.y;              // plant base height (matrix translation.y)
  float above = max(0.0, wp.y - base); // height of this vertex above its base

  // Phototropic lean toward brighter ground (world-space, top bends most).
  float bend = b * b;
  wp.x += instLeanX * bend;
  wp.z += instLeanZ * bend;

  // Coherent breeze: long-wavelength gust waves roll slowly across the land along
  // a prevailing direction. The spatial frequency is LOW, so a whole tree — and
  // its near neighbours — sway together as one unit (only far-apart plants are out
  // of phase, giving a gentle wave rolling over the meadow). No per-plant random
  // term, so the motion is fully synchronised. (Wind is applied here in world
  // space, after the instance's world matrix, so a plant's yaw doesn't change it.)
  vec2 windDir = vec2(0.862, 0.507);            // ~unit prevailing direction
  float phase = dot(wp.xz, windDir);
  float gust = sin(time * 0.45 - phase * 0.060)
             + 0.5 * sin(time * 0.80 - phase * 0.110)
             + 0.5 * sin(time * 0.20 - phase * 0.030); // slow swell
  float sway = gust * 0.45;
  float amp = windStrength * above;             // ~linear in height (tall ≠ whipping)
  wp.x += sway * amp * windDir.x;
  wp.z += sway * amp * windDir.y;
  // gentle cross-sway so motion isn't purely 1-D
  float cross = 0.25 * sin(time * 0.5 - phase * 0.05);
  wp.x += cross * amp * -windDir.y;
  wp.z += cross * amp * windDir.x;

  gl_Position = viewProjection * wp;
  vNormal = normalize((world * vec4(normal, 0.0)).xyz);
  vColor = color;
  vStress = instStress;
  vAO = clamp(above * 1.5, 0.35, 1.0); // darken toward the base
}
`;

Effect.ShadersStore["plantFragmentShader"] = /* glsl */ `
precision highp float;
varying vec3 vNormal;
varying vec4 vColor;
varying float vStress;
varying float vAO;

uniform vec3 sunDir;     // direction the light travels (normalized)
uniform vec3 sunColor;
uniform vec3 ambColor;

void main() {
  vec3 N = normalize(vNormal);
  float ndl = max(0.0, dot(N, -sunDir));
  // Leaf translucency: light coming through the leaf from the far side.
  float back = max(0.0, dot(N, sunDir));
  back = pow(back, 2.0) * 0.7;

  vec3 albedo = vColor.rgb;
  albedo = mix(albedo, vec3(0.72, 0.6, 0.25), clamp(vStress, 0.0, 1.0) * 0.6); // dry yellowing
  vec3 lit = albedo * (ambColor * vAO + sunColor * (ndl + back));
  gl_FragColor = vec4(lit, 1.0);
}
`;

export function createPlantMaterial(scene: Scene): ShaderMaterial {
  const mat = new ShaderMaterial(
    "plant",
    scene,
    "plant",
    {
      attributes: ["position", "normal", "color", "birthLength", "world0", "world1", "world2", "world3", "instStress", "instPhase", "instGrowth", "instLeanX", "instLeanZ"],
      uniforms: ["viewProjection", "time", "windStrength", "sunDir", "sunColor", "ambColor"],
    },
  );
  mat.setVector3("sunDir", new Vector3(-0.42, -0.18, 0.89).normalize()); // low sun
  mat.setColor3("sunColor", new Color3(1.0, 0.78, 0.5).scale(1.4));
  mat.setColor3("ambColor", new Color3(0.42, 0.4, 0.32));
  mat.setFloat("windStrength", 0.06);
  mat.setFloat("time", 0);
  mat.backFaceCulling = false; // plants are double-sided
  return mat;
}
